/**
 * Tableau d'indicateurs GA4 (jour, canal ou pays), comme les tableaux Looker
 * qu'il remplace : une ligne par valeur, le total de la période en pied.
 *
 * Le total est celui que GA4 calcule pour la période, pas la somme des lignes :
 * un utilisateur venu deux jours compte une fois.
 *
 * Quatre colonnes teintées selon la valeur (sessions, taux de paniers, achats,
 * CR%) : on repère d'un coup d'œil les lignes qui pèsent. L'intensité suit la
 * valeur rapportée au maximum de la colonne.
 */

import type { Ga4Metrics } from '@shared/types/ga4';

import { formatDecimal, formatInteger, formatPercent } from '../lib/format';
import { CATEGORICAL_COLORS } from '../lib/theme';
import { Card } from './Card';

export const formatDuration = (seconds: number | null): string => {
  if (seconds === null) return '—';
  const total = Math.round(seconds);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
};

const pct = (value: number | null) => (value !== null ? formatPercent(value) : '—');

const TINTS = {
  sessions: CATEGORICAL_COLORS[4],
  cart: CATEGORICAL_COLORS[3],
  purchases: CATEGORICAL_COLORS[2],
  conversion: CATEGORICAL_COLORS[0],
} as const;

/** Fond d'une cellule : de transparent (0) à 45 % de la couleur (maximum de la colonne). */
const tint = (color: string, value: number | null, max: number) =>
  value !== null && max > 0 ? { background: `color-mix(in srgb, ${color} ${Math.round((value / max) * 45)}%, transparent)` } : undefined;

interface Ga4MetricsTableProps<T extends Ga4Metrics> {
  title: string;
  subtitle: string;
  /** En-tête de la première colonne (Date, Canal, Pays). */
  labelHeader: string;
  rows: readonly T[];
  label: (row: T) => string;
  rowKey: (row: T) => string;
  total: Ga4Metrics | undefined;
  /** Hauteur max avant défilement, en-tête et total restant visibles. */
  maxHeight?: number;
}

export function Ga4MetricsTable<T extends Ga4Metrics>({ title, subtitle, labelHeader, rows, label, rowKey, total, maxHeight = 440 }: Ga4MetricsTableProps<T>) {
  const max = (pick: (row: T) => number | null) => Math.max(0, ...rows.map((row) => pick(row) ?? 0));
  const maxSessions = max((row) => row.sessions);
  const maxCart = max((row) => row.cartRatePercent);
  const maxPurchases = max((row) => row.purchases);
  const maxConversion = max((row) => row.conversionPercent);

  const cells = (row: Ga4Metrics, tinted: boolean) => (
    <>
      <td className="num">{formatInteger(row.users)}</td>
      <td className="num" style={tinted ? tint(TINTS.sessions, row.sessions, maxSessions) : undefined}>
        {formatInteger(row.sessions)}
      </td>
      <td className="num">{row.viewsPerSession !== null ? formatDecimal(row.viewsPerSession) : '—'}</td>
      <td className="num">{formatInteger(row.addToCarts)}</td>
      <td className="num">{pct(row.engagementRatePercent)}</td>
      <td className="num">{formatDuration(row.avgSessionSeconds)}</td>
      <td className="num" style={tinted ? tint(TINTS.cart, row.cartRatePercent, maxCart) : undefined}>
        {pct(row.cartRatePercent)}
      </td>
      <td className="num" style={tinted ? tint(TINTS.purchases, row.purchases, maxPurchases) : undefined}>
        {formatInteger(row.purchases)}
      </td>
      <td className="num" style={tinted ? tint(TINTS.conversion, row.conversionPercent, maxConversion) : undefined}>
        {pct(row.conversionPercent)}
      </td>
    </>
  );

  return (
    <Card title={title} subtitle={subtitle} className="chart-grid__wide">
      {rows.length === 0 ? (
        <p className="empty">Aucune donnée GA4 sur cette période.</p>
      ) : (
        <div className="table-wrap table-scroll" style={{ maxHeight }}>
          <table className="data">
            <thead>
              <tr>
                <th>{labelHeader}</th>
                <th className="num">Utilisateurs</th>
                <th className="num">Sessions</th>
                <th className="num">Vues par session</th>
                <th className="num">Ajouts au panier</th>
                <th className="num">Taux d'engagement</th>
                <th className="num">Durée moyenne</th>
                <th className="num">Tx Paniers</th>
                <th className="num">Achats</th>
                <th className="num">CR%</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={rowKey(row)}>
                  <td className="wrap">{label(row)}</td>
                  {cells(row, true)}
                </tr>
              ))}
            </tbody>
            {total && (
              <tfoot>
                <tr className="row-total">
                  <td>Total</td>
                  {cells(total, false)}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </Card>
  );
}
